import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import { useNavigate } from "react-router-dom"; 
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Spinner,
  Text,
  useToast,
  VStack,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  ListItem,
  UnorderedList,
  Image,
  Container,
  Center,
  Heading,
  Radio,
  RadioGroup,
  Stack
} from "@chakra-ui/react";
import SignaturePad from "react-signature-canvas";
import { motion } from "framer-motion";
import moment from "moment";

const COLORS = {
    isabelline: "#F4EEEA",    // Background, card backgrounds
    maastrichtBlue: "#0E1735", // Headings, important text
    shadowBlue: "#7481AF",    // Secondary elements, borders, labels
    mandarin: "#F17946",      // CTAs, highlights, errors
    shinyShamrock: "#56A27F"  // Success states, secondary actions
};

// PaymentMethodSelection Component
const PaymentMethodSelection = ({ 
  registrationData, 
  registrationID, 
  onSkillsFutureSelect, 
  onCreditCardSelect,
  paymentBalance 
}) => {
  const [selectedMethod, setSelectedMethod] = useState('');
  const [processing, setProcessing] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [showBackdoor, setShowBackdoor] = useState(false);
  const toast = useToast();

  // Calculate SkillsFuture eligibility
  const getSkillsFutureEligibility = () => {
    if (!registrationData?.start_date) return null;
    
    const startDate = moment(registrationData.start_date);
    const today = moment();
    const daysUntilEligible = startDate.diff(today, 'days');
    
    // Backdoor override for testing
    if (showBackdoor) {
      return {
        canPayNow: true,
        daysLeft: 0,
        daysUntilCourse: daysUntilEligible,
        skillsFutureAvailableDate: startDate.subtract(60, 'days')
      };
    }
    
    return {
      canPayNow: daysUntilEligible <= 60,
      daysLeft: daysUntilEligible > 60 ? daysUntilEligible - 60 : 0,
      daysUntilCourse: daysUntilEligible,
      skillsFutureAvailableDate: startDate.subtract(60, 'days')
    };
  };

  const eligibility = getSkillsFutureEligibility();

  // Handle title click for backdoor
  const handleTitleClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    if (newCount === 5) {
      setShowBackdoor(!showBackdoor);
      setClickCount(0);
      toast({
        title: showBackdoor ? "Backdoor Deactivated" : "Backdoor Activated",
        description: showBackdoor ? "Showing normal SkillsFuture eligibility" : "Showing SkillsFuture as available",
        status: "info",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const handleMethodSelect = (value) => {
    // Prevent selection if SkillsFuture is not eligible
    if (value === 'skillsfuture' && !eligibility?.canPayNow) {
      toast({
        title: "SkillsFuture Not Available",
        description: `SkillsFuture payment will be available in ${eligibility.daysLeft} days on ${eligibility.skillsFutureAvailableDate.format("DD MMM YYYY (ddd)")}`,
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    
    // Prevent selection if SkillsFuture has already been claimed
    if (value === 'skillsfuture' && registrationData?.skillsfuture_claimed_amount !== null) {
      toast({
        title: "SkillsFuture Already Claimed",
        description: `You have already claimed SGD $${registrationData.skillsfuture_claimed_amount} from SkillsFuture for this course.`,
        status: "info",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    
    setSelectedMethod(value);
  };

  const handleProceed = async () => {
    if (!selectedMethod) {
      toast({
        title: "Error",
        description: "Please select a payment method before proceeding.",
        status: "error",
        isClosable: true,
        position: "top",
      });
      return;
    }

    setProcessing(true);
    
    if (selectedMethod === 'skillsfuture') {
      await onSkillsFutureSelect();
    } else if (selectedMethod === 'creditcard') {
      await onCreditCardSelect();
    }
    
    setProcessing(false);
  };

  return (
    <Container 
      w={"100%"} 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      bg={COLORS.isabelline} 
      minH="100vh" 
      py={8}
    >
      <Box 
        width="100%" 
        bg="white" 
        maxW={"800px"} 
        px={10} 
        py={10} 
        borderRadius="lg" 
        boxShadow="md"
      >
        <Heading 
          size={"sm"}
          mb={8} 
          color={COLORS.maastrichtBlue} 
          borderBottom="2px solid" 
          borderColor={COLORS.mandarin} 
          pb={2} 
          width="fit-content"
          onClick={handleTitleClick}
          userSelect="none"
        >
          Payment Method Selection
        </Heading>

        <Alert status="info" mb={8}>
          <AlertIcon />
          Please select your preferred payment method to complete your enrollment.
        </Alert>

        <VStack spacing={8} align="stretch">
          {/* Payment Balance Display */}
          <Box 
            p={6} 
            border="2px solid" 
            borderColor={COLORS.shadowBlue} 
            borderRadius="lg"
            bg={COLORS.isabelline}
          >
            <Text fontSize="lg" fontWeight="bold" color={COLORS.maastrichtBlue} mb={2}>
              Outstanding Payment Balance
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color={COLORS.mandarin}>
              SGD ${paymentBalance}
            </Text>
            <Text fontSize="sm" mt={2}>
              If you’ve already made a payment, please wait while we verify it.
            </Text>
            <Text fontSize="sm" mt={2}>
              The page will update automatically once your payment is confirmed.
            </Text>
            <Text fontSize="sm" mt={2}>
              Otherwise, you may proceed to make a payment below.
            </Text>
          </Box>

          {/* Payment Method Selection */}
          <Box>
            <Text fontSize="lg" fontWeight="bold" color={COLORS.maastrichtBlue} mb={4}>
              Select Payment Method
            </Text>
            
            <RadioGroup value={selectedMethod} onChange={handleMethodSelect}>
              <Stack spacing={4}>
                <Box 
                  p={6} 
                  border="2px solid" 
                  borderColor={
                    selectedMethod === 'skillsfuture' ? COLORS.mandarin : 
                    registrationData?.skillsfuture_claimed_amount !== null ? COLORS.shinyShamrock :
                    COLORS.shadowBlue
                  } 
                  borderRadius="lg"
                  bg={
                    selectedMethod === 'skillsfuture' ? `${COLORS.mandarin}10` : 
                    registrationData?.skillsfuture_claimed_amount !== null ? `${COLORS.shinyShamrock}10` :
                    eligibility?.canPayNow ? 'white' : `${COLORS.shadowBlue}10`
                  }
                  cursor={
                    registrationData?.skillsfuture_claimed_amount !== null ? "not-allowed" :
                    eligibility?.canPayNow ? "pointer" : "not-allowed"
                  }
                  onClick={() => handleMethodSelect('skillsfuture')}
                  _hover={
                    registrationData?.skillsfuture_claimed_amount !== null ? {} :
                    eligibility?.canPayNow ? { borderColor: COLORS.mandarin } : {}
                  }
                  opacity={
                    registrationData?.skillsfuture_claimed_amount !== null ? 1 :
                    eligibility?.canPayNow ? 1 : 1.0
                  }
                >
                  <Radio 
                    value="skillsfuture" 
                    colorScheme="orange"
                    isDisabled={!eligibility?.canPayNow || registrationData?.skillsfuture_claimed_amount !== null}
                  >
                    <VStack align="start" spacing={2} ml={2}>
                      <HStack justify="space-between" width="100%">
                        <Text fontWeight="bold" color={
                          registrationData?.skillsfuture_claimed_amount !== null ? COLORS.shinyShamrock :
                          eligibility?.canPayNow ? COLORS.maastrichtBlue : COLORS.shadowBlue
                        }>
                          SkillsFuture Credit
                        </Text>
                        {registrationData?.skillsfuture_claimed_amount !== null ? (
                          <Text 
                            fontSize="xs" 
                            fontWeight="600"
                            color={COLORS.shinyShamrock}
                          >
                            ✅ Already Claimed: SGD ${registrationData.skillsfuture_claimed_amount}
                          </Text>
                        ) : (
                          eligibility && (
                            <Text 
                              fontSize="xs" 
                              fontWeight="600"
                              color={eligibility.canPayNow ? COLORS.shinyShamrock : COLORS.mandarin}
                            >
                              {eligibility.canPayNow 
                                ? `✅ Available (${eligibility.daysUntilCourse} days to course)`
                                : `⏳ In ${eligibility.daysLeft} days on ${eligibility.skillsFutureAvailableDate.format("DD MMM (ddd)")}`
                              }
                            </Text>
                          )
                        )}
                      </HStack>
                      <Text fontSize="sm" color={
                        registrationData?.skillsfuture_claimed_amount !== null ? COLORS.shinyShamrock :
                        eligibility?.canPayNow ? COLORS.shadowBlue : COLORS.shadowBlue
                      }>
                        {registrationData?.skillsfuture_claimed_amount !== null ? 
                          "You have already used your SkillsFuture credits for this course. The claimed amount has been applied to your payment balance." :
                          "Use your SkillsFuture credits to pay for this course. You will be redirected to the SkillsFuture portal to complete the payment."
                        }
                      </Text>
                      {!eligibility?.canPayNow && registrationData?.skillsfuture_claimed_amount === null && (
                        <Text fontSize="sm" fontWeight="600" color={COLORS.mandarin}>
                          Note: SkillsFuture payment is only available within 60 days of course start date.
                        </Text>
                      )}
                    </VStack>
                  </Radio>
                </Box>

                <Box 
                  p={6} 
                  border="2px solid" 
                  borderColor={selectedMethod === 'creditcard' ? COLORS.mandarin : COLORS.shadowBlue} 
                  borderRadius="lg"
                  bg={selectedMethod === 'creditcard' ? `${COLORS.mandarin}10` : 'white'}
                  cursor="pointer"
                  onClick={() => setSelectedMethod('creditcard')}
                  _hover={{ borderColor: COLORS.mandarin }}
                >
                  <Radio value="creditcard" colorScheme="orange">
                    <VStack align="start" spacing={2} ml={2}>
                      <Text fontWeight="bold" color={COLORS.maastrichtBlue}>
                        Credit Card / PayNow
                      </Text>
                      <Text fontSize="sm" color={COLORS.shadowBlue}>
                        Pay directly with your credit card or PayNow. 
                        You will be redirected to our secure payment gateway.
                      </Text>
                    </VStack>
                  </Radio>
                </Box>
              </Stack>
            </RadioGroup>
          </Box>

          {/* Proceed Button */}
          <Button
            bg={COLORS.mandarin}
            color="white"
            size="lg"
            onClick={handleProceed}
            isLoading={processing}
            loadingText="Processing..."
            isDisabled={!selectedMethod}
            _hover={{ bg: '#e06835' }}
            _active={{ bg: '#d55a29' }}
          >
            Proceed with Payment
          </Button>
        </VStack>
      </Box>
    </Container>
  );
};

export const Enrollment = () => {
  const { registrationID } = useParams();
  const [registrationData, setRegistrationData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const sigPad = useRef(null);
  const toast = useToast();
  const navigate = useNavigate();
  const [isSignatureEmpty, setIsSignatureEmpty] = useState(true);
  const [showPaymentSelection, setShowPaymentSelection] = useState(false);
  const [showSkillsFutureForm, setShowSkillsFutureForm] = useState(false);
  const [claimRequest, setClaimRequest] = useState("");
  const [skillsFutureStatus, setSkillsFutureStatus] = useState(null);
  const [showSkillsFutureResult, setShowSkillsFutureResult] = useState(false);
  const [skillsFutureResult, setSkillsFutureResult] = useState(null);
  const [paymentLink, setPaymentLink] = useState(null);
  const [skillsFutureSubmitting, setSkillsFutureSubmitting] = useState(false);
  const [showInlineSuccess, setShowInlineSuccess] = useState(false);

  console.log("Registration ID from URL:", registrationID);

  // Handle SkillsFuture response from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const skillsFutureStatus = urlParams.get('skillsfuture');
    
    if (skillsFutureStatus) {
      if (skillsFutureStatus === 'success') {
        const claimedAmount = urlParams.get('claimed');
        const claimId = urlParams.get('claimId');
        
        setSkillsFutureResult({
          type: 'success',
          claimedAmount: parseFloat(claimedAmount || 0),
          claimId: claimId,
          message: `SkillsFuture claim successful! Amount claimed: SGD $${claimedAmount}`
        });
        setShowSkillsFutureResult(true);
        
        // Show success toast
        toast({
          title: "SkillsFuture Claim Successful",
          description: `Successfully claimed SGD $${claimedAmount} from SkillsFuture.`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
        
        // Refresh registration data to get updated payment balance and check for completion
        fetchRegistrationAndCheckCompletion();
        
      } else if (skillsFutureStatus === 'failed' || skillsFutureStatus === 'error') {
        const message = urlParams.get('message') || 'SkillsFuture claim was not successful';
        
        setSkillsFutureResult({
          type: 'error',
          message: message
        });
        setShowSkillsFutureResult(true);
        
        // Show error toast
        toast({
          title: "SkillsFuture Claim Failed",
          description: message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
      
      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [toast]);

  // Fetch registration data and check for completion
  const fetchRegistrationAndCheckCompletion = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("full_registration_details_with_enrollment")
        .select(
          "id, participant_name, participant_email, name, start_date, end_date, price, course_run_id, registration_type, registration_stage, billing_type, birth_age, nationality, course_dates,code, course_objectives,course_duration,minimum_entry_requirements,prerequisites,funding_validity_period, payment_balance, signature, ssg_course_id, nric, mobile_no,ssg_claim_doc_submitted, skillsfuture_claimed_amount, skillsfuture_claim_id"
        )
        // .gte("start_date", new Date().toLocaleDateString('fr-CA'))
        .eq("id", registrationID)
        .single();

      if (error) throw error;

      setRegistrationData(data);
      console.log(data);

      // Check for completion conditions after successful SkillsFuture payment
      if (data.payment_balance === 0) {
        if (data.ssg_claim_doc_submitted === null  && data.skillsfuture_claim_id) {
          // Send trigger to pipedream
          await sendPipedreamTrigger(registrationID);
        } else if (data.ssg_claim_doc_submitted === true) {
          // Show inline success message
          setShowInlineSuccess(true);
          setShowSkillsFutureResult(false);
          setShowPaymentSelection(false);
        }
      }
    } catch (error) {
      console.error("Error fetching registration:", error);
      toast({
        title: "Error",
        description: "Could not load registration details.",
        status: "error",
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

    // Fetch registration data
  const fetchRegistration = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("full_registration_details_with_enrollment")
        .select(
          "id, participant_name, participant_email, name, start_date, end_date, price, course_run_id, registration_type, registration_stage, billing_type, birth_age, nationality, course_dates,code, course_objectives,course_duration,minimum_entry_requirements,prerequisites,funding_validity_period, payment_balance, signature, ssg_course_id, nric, mobile_no,ssg_claim_doc_submitted, skillsfuture_claimed_amount, skillsfuture_claim_id"
        )
        // .gte("start_date", new Date().toLocaleDateString('fr-CA'))
        .eq("id", registrationID)
        .single();

      if (error) throw error;

      setRegistrationData(data);
      console.log(data);

      // Check for completion conditions on initial load
      if (data.payment_balance === 0) {
        if (data.ssg_claim_doc_submitted === null  && data.skillsfuture_claim_id) {
          // Send trigger to pipedream
          await sendPipedreamTrigger(registrationID);
          setShowInlineSuccess(true);
        } else if (data.ssg_claim_doc_submitted === true) {
          // Show inline success message
          setShowInlineSuccess(true);
          setShowSkillsFutureResult(false);
          setShowPaymentSelection(false);
        }
      }
    } catch (error) {
      console.error("Error fetching registration:", error);
      toast({
        title: "Error",
        description: "Could not load registration details.",
        status: "error",
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  // Send trigger to pipedream
  const sendPipedreamTrigger = async (registrationId) => {
    try {
      const response = await fetch('https://eohp67h6n9o17wj.m.pipedream.net', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            object: {
              client_reference_id: registrationId
            }
          }
        }),
      });

      if (response.ok) {
        console.log('Pipedream trigger sent successfully');
        toast({
          title: "Processing Complete",
          description: "Your enrollment has been processed successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      } else {
        throw new Error('Failed to send pipedream trigger');
      }
    } catch (error) {
      console.error('Error sending pipedream trigger:', error);
      toast({
        title: "Processing Error",
        description: "There was an issue processing your enrollment. Please contact support.",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  };

  useEffect(() => {
    // Call immediately
    fetchRegistration();

    // Set interval polling
    const interval = setInterval(() => {
      console.log("polling")
      if (registrationData?.signature) {
        fetchRegistration();
      }
    }, 10000);

    // Clean up on unmount or when registrationID/toast changes
    return () => clearInterval(interval);
  }, [registrationID, toast]);


  //console.log(registrationData)

  // Check if signature exists and payment balance > 0
  useEffect(() => {
    if (registrationData && registrationData.signature && registrationData.payment_balance > 0) {
      setShowPaymentSelection(true);
    }
  }, [registrationData]);

  // Handle SkillsFuture payment selection
  const handleSkillsFutureSelect = async () => {
    setSkillsFutureStatus({ type: 'info', message: 'Processing your request...' });

    try {
      // Call the relay-ssg function for SkillsFuture
      const payload = {
        webhookData: {
          courseId: registrationData.ssg_course_id,
          courseFee: registrationData.payment_balance,
          startDate: registrationData.start_date,
          nric: registrationData.nric,
          email: registrationData.participant_email,
          homeNumber: registrationData.mobile_no, // You might need to add this field to your query
          mobileNumber: registrationData.mobile_no, // You might need to add this field to your query
          additionalInformation: "registration_id: " + registrationID
        },
        apiType: "Submit Claim",
        timestamp: new Date().toISOString(),
        source: 'sozcode-portal'
      };

      console.log('Sending request to relay-ssg:', payload);

      const { data, error } = await supabase.functions.invoke('relay-ssg', {
        body: payload,
        method: 'POST',
        mode: 'cors'
      });

      if (error) {
        throw new Error(`Relay failed: ${error.message}`);
      }

      console.log('Received response from relay-ssg:', data);

      if (data?.claim_request) {
        setClaimRequest(data.claim_request);
        setShowSkillsFutureForm(true);
        setSkillsFutureStatus({ type: 'success', message: 'Request processed successfully! Please review and submit to continue to SkillsFuture.' });
        
        toast({
          title: "Success",
          description: "Your claim request has been processed. Please submit the form below to continue to SkillsFuture.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error('No claim_request received from relay-ssg');
      }
    } catch (error) {
      console.error('SkillsFuture error:', error);
      
      setSkillsFutureStatus({ type: 'error', message: `Error: ${error.message}` });
      
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle SkillsFuture form submission
  const handleSkillsFutureSubmit = (e) => {
    e.preventDefault();
    
    setSkillsFutureSubmitting(true);
    
    // Create and submit the form to SkillsFuture
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = 'https://sfc.myskillsfuture.gov.sg/sfc2-ind/api/individual/sfcpayment/claim/submit/gateway';

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'encryptedPayload';
    input.value = claimRequest;

    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  // Handle Credit Card / PayNow payment selection
  const handleCreditCardSelect = async () => {
    try {
      toast({
        title: "Redirecting to payment gateway",
        description: "Please wait as we redirect you to the payment page",
        status: "info",
        isClosable: true,
        position: "top",
      });

      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          product_id: registrationData.code,
          registration_id: registrationID,
          participant_email: registrationData.participant_email,
          birth_age: registrationData.birth_age,
          nationality: registrationData.nationality,
          payment_balance: registrationData.payment_balance,
          course_name: registrationData.name,
          course_start_date: moment(registrationData.start_date).format("DD MMM YYYY")
        },
      });

      if (error) {
        throw new Error(`Failed to create checkout session: ${error.message}`);
      }

      if (!data?.url) {
        throw new Error("Checkout URL not found in the response.");
      }

      // Track begin_checkout event in Google Analytics
      if (window.gtag) {
        window.gtag('event', 'begin_checkout', {
          value: registrationData.payment_balance,
          currency: 'SGD',
          items: [{
            item_id: registrationData.code,
            item_name: registrationData.name,
            price: registrationData.payment_balance
          }]
        });
      }

      // Track InitiateCheckout event in Meta Pixel
      if (window.fbq) {
        window.fbq('track', 'InitiateCheckout', {
          value: registrationData.payment_balance,
          currency: 'SGD'
        });
      }

      // Redirect to checkout page
      window.location.href = data.url;
    } catch (error) {
      console.error('Credit card payment error:', error);
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  };

  // Save Signature and Proceed
  const saveSignatureAndProceed = async () => {
    if (isSignatureEmpty) {
      toast({
        title: "Error",
        description: "Please sign the contract before proceeding.",
        status: "error",
        isClosable: true,
        position: "top",
      });
      return;
    }
  
    setSubmitting(true);
    try {
      // Save signature
      const signature = sigPad.current.getTrimmedCanvas().toDataURL("image/png");
      const { error: updateError } = await supabase
        .from("registration")
        .update({ signature })
        .eq("id", registrationID);
  
      if (updateError) {
        throw new Error("Failed to update signature in Supabase");
      }
  
      toast({
        title: "Signature Saved",
        description: "Your signature has been saved successfully. Please select your payment method.",
        status: "success",
        isClosable: true,
        position: "top",
      });
      
      // Show payment method selection instead of redirecting to Stripe
      setShowPaymentSelection(true);
  
    } catch (error) {
      console.error("Error during signature save:", {
        message: error.message,
        stack: error.stack,
      });
  
      toast({
        title: "Error during process",
        description: "Failed to save signature. Please try again.",
        status: "error",
        duration: 9000,
        isClosable: true,
        position: "top",
      });

      // Add a second toast with more specific error information if available
      if (error.message.includes("Failed to update signature")) {
        toast({
          title: "Signature Error",
          description: "Could not save signature to Supabase. Please try again.",
          status: "error",
          duration: 9000,
          isClosable: true,
          position: "top",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Start Stripe Checkout
  const startStripeCheckout = async () => {
    try {
      toast({
        title: "Redirecting to Stripe payment page",
        description: "Please wait as we direct you to the payment page",
        status: "info",
        isClosable: true,
        position: "top",
      });

      // Call the Supabase Edge Function with detailed logging
      console.log('Sending request to Edge Function with data:', {
        product_id: registrationData.code,
        registration_id: registrationID,
        participant_email: registrationData.participant_email,
        birth_age: registrationData.birth_age,
        nationality: registrationData.nationality,
        course_name: registrationData.name,
        course_start_date: moment(registrationData.start_date).format("DD MMM YYYY")
      });

      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          product_id: registrationData.code,
          registration_id: registrationID,
          participant_email: registrationData.participant_email,
          birth_age: registrationData.birth_age,
          nationality: registrationData.nationality,
          payment_balance: registrationData.payment_balance,
          course_name: registrationData.name,
          course_start_date: moment(registrationData.start_date).format("DD MMM YYYY")
        },
      });

      // Log the response
      console.log('Edge Function response:', { data, error });

      if (error) {
        console.error('Detailed error information:', error);
        throw new Error(`Failed to create Stripe checkout session: ${error.message}`);
      }

      if (!data?.url) {
        console.error('Invalid response format:', data);
        throw new Error("Stripe checkout URL not found in the response.");
      }

      // Track begin_checkout event in Google Analytics
      if (window.gtag) {
        window.gtag('event', 'begin_checkout', {
          value: registrationData.payment_balance,
          currency: 'SGD',
          items: [{
            item_id: registrationData.code,
            item_name: registrationData.name,
            price: registrationData.payment_balance
          }]
        });
        console.log('Google Analytics begin_checkout event tracked');
      }

      // Track InitiateCheckout event in Meta Pixel
      if (window.fbq) {
        window.fbq('track', 'InitiateCheckout', {
          value: registrationData.payment_balance,
          currency: 'SGD'
        });
        console.log('Meta Pixel InitiateCheckout event tracked');
      }

      // Redirect to Stripe checkout page
      window.location.href = data.url;

    } catch (error) {
      console.error("Error during process:", {
        message: error.message,
        stack: error.stack,
        requestData: {
          product_id: registrationData.code,
          registration_id: registrationID,
          participant_email: registrationData.participant_email,
          birth_age: registrationData.birth_age,
          nationality: registrationData.nationality,
          course_name: registrationData.name,
          course_start_date: moment(registrationData.start_date).format("DD MMM YYYY")
        }
      });

      // Keep existing toast for user feedback
      toast({
        title: "Error during process",
        description: "- Error: Failed to create Stripe checkout session",
        status: "error",
        duration: 9000,
        isClosable: true,
        position: "top",
      });

    } finally {
      setSubmitting(false);
    }
  };


  // Clear Signature
  const clearSignature = () => {
    sigPad.current.clear();
    setIsSignatureEmpty(true);
  };

  if (loading) {
    return (
      <Center h="100vh" bg={COLORS.isabelline}>
        <Spinner size="xl" thickness="4px" speed="0.65s" color={COLORS.mandarin} />
      </Center>
    );
  }

  if (!registrationData) {
    return (
      <Center h="100vh" bg={COLORS.isabelline}>
        <Text>No registration data found.</Text>
      </Center>
    );
  }

  // Show inline success message if enrollment is complete
  if (showInlineSuccess) {
    return (
      <Box
        w={"100%"} 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        bg={COLORS.isabelline} 
        minH="100vh" 
        py={8}
      >
        <Box 
          width="100%" 
          bg="white" 
          px={10}
          py={10}
          borderRadius="lg" 
          boxShadow="md"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <VStack spacing={8} w="full">
              <motion.div
                initial={{ y: -50 }}
                animate={{ y: 0 }}
                transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
              >
                <Heading 
                  size={"sm"} 
                  color={COLORS.maastrichtBlue}
                  borderBottom="2px solid"
                  borderColor={COLORS.shinyShamrock}
                  pb={2}
                >
                  🎉 Enrollment Complete!
                </Heading>
              </motion.div>

              <motion.div
                initial={{ x: 100 }}
                animate={{ x: 0 }}
                transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
              >
                <Text fontSize="md" color={COLORS.maastrichtBlue}>
                  Thank you for your payment, <strong>{registrationData?.participant_name}</strong>. Your enrollment for this course is confirmed.
                </Text>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 1, ease: "easeOut" }}
              >
                <Text fontSize="md" color={COLORS.maastrichtBlue}>
                  You will receive the signed student contract via your email <Text as="span" color={COLORS.mandarin} fontWeight="bold">{registrationData.participant_email}</Text> soon.
                </Text>
                <br/>
                <Text fontSize="md" fontWeight="bold" color={COLORS.maastrichtBlue} textAlign="center">
                  See you for the course!
                </Text>
              </motion.div>

              {/* Course Details Section */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 1, ease: "easeOut" }}
              >
                <Box
                  bg={COLORS.isabelline}
                  border="1px solid"
                  borderColor={COLORS.shadowBlue}
                  borderRadius="md"
                  p={6}
                  boxShadow="sm"
                >
                  <Text fontSize="lg" fontWeight="bold" mb={4} color={COLORS.maastrichtBlue}>
                    {registrationData?.name}
                  </Text>
                  <Table variant="simple" size="sm" width="100%" bg="white">
                    <Thead bg={COLORS.isabelline}>
                      <Tr>
                        <Th textAlign="center" border="1px solid" borderColor={COLORS.shadowBlue} bg={COLORS.isabelline} color={COLORS.maastrichtBlue}>Date</Th>
                        <Th textAlign="center" border="1px solid" borderColor={COLORS.shadowBlue} bg={COLORS.isabelline} color={COLORS.maastrichtBlue}>Start Time</Th>
                        <Th textAlign="center" border="1px solid" borderColor={COLORS.shadowBlue} bg={COLORS.isabelline} color={COLORS.maastrichtBlue}>End Time</Th>
                      </Tr>
                    </Thead>
                    <Tbody bg="white">
                      {registrationData?.course_dates.map((session, index) => (
                        <Tr key={index} bg="white">
                          <Td textAlign="center" border="1px solid" borderColor={COLORS.shadowBlue} bg="white" color={COLORS.maastrichtBlue}>
                            {moment(session.date, "DD.MM.YYYY").format("DD MMM YYYY (ddd)")}
                          </Td>
                          <Td textAlign="center" border="1px solid" borderColor={COLORS.shadowBlue} bg="white" color={COLORS.maastrichtBlue}>
                            {moment(session.start_time, "HHmm").format("h:mm A")}
                          </Td>
                          <Td textAlign="center" border="1px solid" borderColor={COLORS.shadowBlue} bg="white" color={COLORS.maastrichtBlue}>
                            {moment(session.end_time, "HHmm").format("h:mm A")}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  <Text mt={4} textAlign="left" color={COLORS.maastrichtBlue}>
                    <b>Venue:</b> Habitat Learning Centre, 1 Goldhill Plaza, #02-27, Singapore 308899. The nearest MRT is Novena.
                  </Text>
                  <Text mt={4} textAlign="left" color={COLORS.maastrichtBlue}>
                    <b>Directions:</b>{" "}
                    <a
                      href="https://sozcode.com/goldhill-plaza-how-to-get-there"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: COLORS.mandarin, textDecoration: "underline" }}
                    >
                      https://sozcode.com/goldhill-plaza-how-to-get-there
                    </a>
                  </Text>
                  <Text mt={4} textAlign="left" color={COLORS.maastrichtBlue}>
                    <b>Duration:</b> {registrationData?.course_duration} hours
                  </Text>
                </Box>
              </motion.div>
            </VStack>
          </motion.div>
        </Box>
      </Box>
    );
  }

  // Show SkillsFuture form if claim request is ready
  if (showSkillsFutureForm) {
    return (
      <Container 
        w={"100%"} 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        bg={COLORS.isabelline} 
        minH="100vh" 
        py={8}
        px={{ base: 4, md: 8 }}
      >
        <Box 
          width="100%" 
          bg="white" 
          maxW={{ base: "100%", md: "800px" }} 
          px={{ base: 4, md: 8 }} 
          py={{ base: 6, md: 8 }} 
          borderRadius="lg" 
          boxShadow="md"
        >
          <Heading 
            size={{ base: "sm", md: "md" }}
            mb={{ base: 6, md: 8 }} 
            color={COLORS.maastrichtBlue} 
            borderBottom="2px solid" 
            borderColor={COLORS.mandarin} 
            pb={2} 
            width="fit-content"
          >
            SkillsFuture Credit Pay Claim
          </Heading>

          <Box>
            <Alert status="info" mb={6} borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Ready to Submit to SkillsFuture</Text>
                <Text fontSize="sm">
                  Your claim request has been processed successfully. When you click submit below, 
                  you will be redirected to the SkillsFuture website to make payment under your SkillsFuture credits.
                </Text>
              </Box>
            </Alert>

            {skillsFutureStatus && (
              <Alert status={skillsFutureStatus.type} mb={6} borderRadius="md">
                <AlertIcon />
                {skillsFutureStatus.message}
              </Alert>
            )}

            <form onSubmit={handleSkillsFutureSubmit}>
              <VStack spacing={6} align="stretch">
                <Box>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue} mb={2}>
                    Instructions:
                  </Text>
                  <Text color={COLORS.maastrichtBlue} mb={4}>
                    Click the submit button below to be redirected to the SkillsFuture website where you can 
                    complete your payment using your SkillsFuture credits. Your claim data has been securely 
                    prepared and will be automatically submitted to SkillsFuture.
                  </Text>
                </Box>

                {/* Hidden input with the claim request */}
                <input
                  type="hidden"
                  name="encryptedPayload"
                  value={claimRequest}
                />

                <Button
                  type="submit"
                  bg={COLORS.mandarin}
                  color="white"
                  size="lg"
                  _hover={{ bg: '#e06835' }}
                  isLoading={skillsFutureSubmitting}
                  loadingText="Submitting..."
                >
                  Continue to SkillsFuture
                </Button>

                <Button
                  variant="outline"
                  color={COLORS.maastrichtBlue}
                  borderColor={COLORS.maastrichtBlue}
                  onClick={() => {
                    setShowSkillsFutureForm(false);
                    setClaimRequest("");
                    setSkillsFutureStatus(null);
                  }}
                  _hover={{ bg: COLORS.maastrichtBlue, color: "white" }}
                >
                  Back to Payment Selection
                </Button>
              </VStack>
            </form>
          </Box>
        </Box>
      </Container>
    );
  }

  // Show SkillsFuture result if available
  if (showSkillsFutureResult) {
    return (
      <Container 
        w={"100%"} 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        bg={COLORS.isabelline} 
        minH="100vh" 
        py={8}
        px={{ base: 4, md: 8 }}
      >
        <Box 
          width="100%" 
          bg="white" 
          maxW={{ base: "100%", md: "800px" }} 
          px={{ base: 4, md: 8 }} 
          py={{ base: 6, md: 8 }} 
          borderRadius="lg" 
          boxShadow="md"
        >
          <Heading 
            size={{ base: "sm", md: "md" }}
            mb={{ base: 6, md: 8 }} 
            color={COLORS.maastrichtBlue} 
            borderBottom="2px solid" 
            borderColor={COLORS.mandarin} 
            pb={2} 
            width="fit-content"
          >
            SkillsFuture Claim Result
          </Heading>

          <Box>
            <Alert 
              status={skillsFutureResult.type} 
              mb={6} 
              borderRadius="md"
            >
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">
                  {skillsFutureResult.type === 'success' ? 'Claim Successful!' : 'Claim Failed'}
                </Text>
                <Text fontSize="sm">
                  {skillsFutureResult.message}
                </Text>
                {skillsFutureResult.type === 'success' && skillsFutureResult.claimedAmount > 0 && (
                  <Text fontSize="sm" mt={2}>
                    Claim ID: {skillsFutureResult.claimId}
                  </Text>
                )}
              </Box>
            </Alert>

            <VStack spacing={6} align="stretch">
              {skillsFutureResult.type === 'success' && registrationData && (
                <Box>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue} mb={2}>
                    Payment Summary:
                  </Text>
                  <Text color={COLORS.maastrichtBlue}>
                    • SkillsFuture Claimed: SGD ${skillsFutureResult.claimedAmount}
                  </Text>
                  <Text color={COLORS.maastrichtBlue}>
                    • Remaining Balance: SGD ${registrationData.payment_balance}
                  </Text>
                </Box>
              )}

              <VStack spacing={4}>
                {skillsFutureResult.type === 'success' && registrationData && registrationData.payment_balance > 0 && (
                  <Button
                    bg={COLORS.mandarin}
                    color="white"
                    size="lg"
                    onClick={handleCreditCardSelect}
                    _hover={{ bg: '#e06835' }}
                  >
                    Proceed to Pay Remaining Balance
                  </Button>
                )}

                {skillsFutureResult.type === 'success' && registrationData && registrationData.payment_balance <= 0 && (
                  <Button
                    bg={COLORS.shinyShamrock}
                    color="white"
                    size="lg"
                    onClick={() => navigate('/success')}
                    _hover={{ bg: '#4a8f6b' }}
                  >
                    Payment Complete - View Course Details
                  </Button>
                )}

                {skillsFutureResult.type === 'error' && (
                  <Button
                    bg={COLORS.mandarin}
                    color="white"
                    size="lg"
                    onClick={() => {
                      setShowSkillsFutureResult(false);
                      setShowPaymentSelection(true);
                    }}
                    _hover={{ bg: '#e06835' }}
                  >
                    Try Alternative Payment Method
                  </Button>
                )}

                {!paymentLink ? (
                  <Button
                    variant="outline"
                    color={COLORS.maastrichtBlue}
                    borderColor={COLORS.maastrichtBlue}
                    onClick={async () => {
                      try {
                        // Create a Stripe checkout session for sharing
                        const { data, error } = await supabase.functions.invoke("create-checkout-session", {
                          body: {
                            product_id: registrationData.code,
                            registration_id: registrationID,
                            participant_email: registrationData.participant_email,
                            birth_age: registrationData.birth_age,
                            nationality: registrationData.nationality,
                            payment_balance: registrationData.payment_balance,
                            course_name: registrationData.name,
                            course_start_date: moment(registrationData.start_date).format("DD MMM YYYY")
                          },
                        });

                        if (error) throw error;

                        setPaymentLink(data.url);
                        
                        toast({
                          title: "Payment Link Created!",
                          description: "Your payment link is ready. You can copy it or share it with others.",
                          status: "success",
                          duration: 3000,
                          isClosable: true,
                          position: "top",
                        });
                      } catch (error) {
                        console.error('Error creating payment link:', error);
                        toast({
                          title: "Error",
                          description: "Failed to create payment link. Please try again.",
                          status: "error",
                          duration: 3000,
                          isClosable: true,
                          position: "top",
                        });
                      }
                    }}
                    _hover={{ bg: COLORS.maastrichtBlue, color: "white" }}
                  >
                    Create Payment Link
                  </Button>
                ) : (
                  <VStack spacing={3} width="100%">
                    <Box 
                      p={3} 
                      border="1px solid" 
                      borderColor={COLORS.shadowBlue} 
                      borderRadius="md"
                      bg={COLORS.isabelline}
                      width="100%"
                    >
                      <Text fontSize="sm" color={COLORS.maastrichtBlue} fontWeight="bold" mb={2}>
                        Payment Link:
                      </Text>
                      <Text fontSize="xs" color={COLORS.shadowBlue} wordBreak="break-all">
                        {paymentLink}
                      </Text>
                    </Box>
                    <HStack spacing={2} width="100%">
                      <Button
                        size="sm"
                        bg={COLORS.mandarin}
                        color="white"
                        onClick={() => window.open(paymentLink, '_blank')}
                        _hover={{ bg: '#e06835' }}
                        flex={1}
                      >
                        Open Payment Page
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        color={COLORS.maastrichtBlue}
                        borderColor={COLORS.maastrichtBlue}
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(paymentLink);
                            toast({
                              title: "Link Copied!",
                              description: "Payment link copied to clipboard.",
                              status: "success",
                              duration: 2000,
                              isClosable: true,
                              position: "top",
                            });
                          } catch (error) {
                            toast({
                              title: "Error",
                              description: "Failed to copy link.",
                              status: "error",
                              duration: 2000,
                              isClosable: true,
                              position: "top",
                            });
                          }
                        }}
                        _hover={{ bg: COLORS.maastrichtBlue, color: "white" }}
                      >
                        Copy
                      </Button>
                    </HStack>
                  </VStack>
                )}
              </VStack>
            </VStack>
          </Box>
        </Box>
      </Container>
    );
  }

  // Show PaymentMethodSelection if signature exists and payment balance > 0
  if (showPaymentSelection) {
    return (
      <PaymentMethodSelection
        registrationData={registrationData}
        registrationID={registrationID}
        onSkillsFutureSelect={handleSkillsFutureSelect}
        onCreditCardSelect={handleCreditCardSelect}
        paymentBalance={registrationData.payment_balance}
      />
    );
  }

  if (!registrationData.signature) {
    return (
        <Container
            w={"100%"}
            display="flex"
            justifyContent="center"
            alignItems="center"
            bg={COLORS.isabelline}
            minH="100vh"
            py={8}
        >
          <Box
              width="100%"
              bg="white"
              maxW={"100%"}
              px={10}
              py={10}
              borderRadius="lg"
              boxShadow="md"
          >
            <Heading
                size={"sm"}
                mb={10}
                color={COLORS.maastrichtBlue}
                borderBottom="2px solid"
                borderColor={COLORS.mandarin}
                pb={2}
                width="fit-content"
            >
              Student Contract
            </Heading>

            <Alert status="info" mb={8}>
              <AlertIcon/>
              Please view the terms of the Student Contract carefully, and sign at the bottom of this page before
              proceeding to checkout.
            </Alert>

            <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.3}}
            >
              <Text fontSize="lg" as="b" color={COLORS.maastrichtBlue}>
                STUDENT CONTRACT
              </Text>
              <Divider my={4} borderColor={COLORS.maastrichtBlue}/>
              <Text color={COLORS.maastrichtBlue}>
                This contract binds the Training Provider and the Student once both parties sign this Contract.
              </Text>
            </motion.div>

            <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.3}}
            >
              <VStack align="start" spacing={4} mt={6}>
                <Text fontWeight="bold" color={COLORS.maastrichtBlue}>a) Registered Name of Training Provider:</Text>
                <Text color={COLORS.maastrichtBlue}>SOZCODE PTE. LTD.</Text>
                <Text color={COLORS.maastrichtBlue}>Registration Number: 202139596G</Text>
              </VStack>
            </motion.div>
            <br/>

            <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.3}}
            >
              <VStack align="start" spacing={4}>
                <Text fontWeight="bold" color={COLORS.maastrichtBlue}>b) Student Name:</Text>
                <Text color={COLORS.maastrichtBlue}>{registrationData.participant_name}</Text>
              </VStack>
            </motion.div>

            <Divider my={6}/>
            <Text fontSize="lg" fontWeight="bold" color={COLORS.maastrichtBlue}>
              1. Course Information
            </Text>
            <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.3}}
            >
              <VStack align="start" spacing={3} mt={4}>
                <VStack alignItems={"flex-start"}>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue}>a) Course Title:</Text>
                  <Text color={COLORS.maastrichtBlue}>{registrationData.name}</Text>
                </VStack>
                <VStack alignItems={"flex-start"}>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue}>b) Course Objectives:</Text>
                  <Text color={COLORS.maastrichtBlue}>
                    {
                      registrationData?.course_objectives
                          ?
                          registrationData.course_objectives
                          :
                          "N.A."
                    }
                  </Text>
                </VStack>
                <VStack alignItems={"flex-start"}>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue}>c) Course Duration:</Text>
                  <Text color={COLORS.maastrichtBlue}>{registrationData.course_duration} hours</Text>
                </VStack>
                <VStack alignItems={"flex-start"}>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue}>d) Full-time or Part-time Course:</Text>
                  <Text color={COLORS.maastrichtBlue}>Part-time</Text>
                </VStack>
                <VStack alignItems={"flex-start"}>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue}>e) Attendance Requirements:</Text>
                  <Text color={COLORS.maastrichtBlue}>&gt;75%</Text>
                </VStack>
                <VStack align="start" spacing={4}>
                  <VStack alignItems={"flex-start"}>
                    <Text fontWeight="bold" color={COLORS.maastrichtBlue}>f) Course Start Date:</Text>
                    <Text
                        color={COLORS.maastrichtBlue}>{moment(registrationData.start_date).format("DD MMM YYYY (ddd)")}</Text>
                  </VStack>
                  <VStack alignItems={"flex-start"}>
                    <Text fontWeight="bold" color={COLORS.maastrichtBlue}>g) Course End Date:</Text>
                    <Text
                        color={COLORS.maastrichtBlue}>{moment(registrationData.end_date).format("DD MMM YYYY (ddd)")}</Text>
                  </VStack>
                  <Text fontWeight="bold" mt={0} color={COLORS.maastrichtBlue}>Course Schedule:</Text>
                  <Box maxW={{base: "80%", md: "100%"}} overflowX="auto" mt={0}>
                    <Table
                        variant="simple"
                        size="sm"
                        width="100%"
                        border="1px solid"
                        borderColor={COLORS.maastrichtBlue}
                        bg="white"
                        color={COLORS.maastrichtBlue}
                    >
                      <Thead bg={COLORS.isabelline}>
                        <Tr>
                          <Th textAlign="center" border="1px solid" borderColor={COLORS.maastrichtBlue}
                              color={COLORS.maastrichtBlue} bg={COLORS.isabelline}>Date</Th>
                          <Th textAlign="center" border="1px solid" borderColor={COLORS.maastrichtBlue}
                              color={COLORS.maastrichtBlue} bg={COLORS.isabelline}>Start Time</Th>
                          <Th textAlign="center" border="1px solid" borderColor={COLORS.maastrichtBlue}
                              color={COLORS.maastrichtBlue} bg={COLORS.isabelline}>End Time</Th>
                        </Tr>
                      </Thead>
                      <Tbody bg="white">
                        {registrationData.course_dates.map((session, index) => (
                            <Tr key={index} bg="white">
                              <Td textAlign="center" border="1px solid" borderColor={COLORS.maastrichtBlue}
                                  color={COLORS.maastrichtBlue} bg="white">
                                {moment(session.date, "DD.MM.YYYY").format("DD MMM YYYY (ddd)")}
                              </Td>
                              <Td textAlign="center" border="1px solid" borderColor={COLORS.maastrichtBlue}
                                  color={COLORS.maastrichtBlue} bg="white">
                                {moment(session.start_time, "HHmm").format("h:mm A")}
                              </Td>
                              <Td textAlign="center" border="1px solid" borderColor={COLORS.maastrichtBlue}
                                  color={COLORS.maastrichtBlue} bg="white">
                                {moment(session.end_time, "HHmm").format("h:mm A")}
                              </Td>
                            </Tr>
                        ))}
                      </Tbody>
                    </Table>

                  </Box>
                </VStack>
                <HStack>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue}>h) Modes of Training:</Text>
                  <Text color={COLORS.maastrichtBlue}>In-person classroom teaching</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue}>i) Minimum entry requirement(s):</Text>
                  <Text color={COLORS.maastrichtBlue}>
                    {
                      registrationData?.minimum_entry_requirements
                          ?
                          registrationData.minimum_entry_requirements
                          :
                          "N.A."
                    }
                  </Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue}>j) Prerequisites:</Text>
                  <Text color={COLORS.maastrichtBlue}>
                    {
                      registrationData?.prerequisities
                          ?
                          registrationData.prerequisities
                          :
                          "N.A."
                    }
                  </Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue}>k) Registered Training Provider:</Text>
                  <Text color={COLORS.maastrichtBlue}>Sozcode</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue}>l) Organisation which awards/confers the
                    qualitifcation:</Text>
                  <Text color={COLORS.maastrichtBlue}>Sozcode</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue}>m) Funding Validity Period:</Text>
                  <Text color={COLORS.maastrichtBlue}>
                    {
                      registrationData?.funding_validity_period
                          ?
                          registrationData.funding_validity_period
                          :
                          "N.A."
                    }
                  </Text>
                </HStack>
              </VStack>
            </motion.div>

            <Divider my={6}/>
            <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.3}}
            >
              <Text fontSize="lg" fontWeight="bold" color={COLORS.maastrichtBlue}>
                2. Course Fees
              </Text>
              <Box overflowX="auto" width="100%" mt={4}>
                <Table
                    variant="simple"
                    size="sm"
                    width="100%"
                    border="1px solid"
                    borderColor={COLORS.maastrichtBlue}
                    bg="white"
                    color={COLORS.maastrichtBlue}
                >
                  <Thead>
                    <Tr bg={COLORS.isabelline}>
                      <Th textAlign="center" border="1px solid" borderColor={COLORS.maastrichtBlue}
                          color={COLORS.maastrichtBlue} bg={COLORS.isabelline}>Fees Breakdown</Th>
                      <Th textAlign="center" border="1px solid" borderColor={COLORS.maastrichtBlue}
                          color={COLORS.maastrichtBlue} bg={COLORS.isabelline}>Total Payable ($)</Th>
                    </Tr>
                  </Thead>
                  <Tbody bg="white">
                    <Tr bg="white">
                      <Td border="1px solid" borderColor={COLORS.maastrichtBlue} color={COLORS.maastrichtBlue}
                          bg="white">Course Fees (Before GST)</Td>
                      <Td textAlign="center" border="1px solid" borderColor={COLORS.maastrichtBlue}
                          color={COLORS.maastrichtBlue} bg="white">{registrationData.price}</Td>
                    </Tr>
                    {/*<Tr>*/}
                    {/*  <Td border="1px solid" borderColor={COLORS.maastrichtBlue}>9% GST</Td>*/}
                    {/*  <Td textAlign="center" border="1px solid" borderColor={COLORS.maastrichtBlue}>{(0.09 * registrationData.price)}</Td>*/}
                    {/*</Tr>*/}
                    <Tr bg="white">
                      <Td border="1px solid" borderColor={COLORS.maastrichtBlue} color={COLORS.maastrichtBlue}
                          bg="white">Less: 50% Baseline Funding</Td>
                      <Td textAlign="center" border="1px solid" borderColor={COLORS.maastrichtBlue}
                          color={COLORS.maastrichtBlue} bg="white">
                        {(registrationData.nationality === "Singapore Citizen" ||
                            registrationData.nationality === "Singapore Permanent Resident" ||
                            registrationData.nationality === "Long Term Visitor Pass+ (LTVP+)")
                            ? `-${(0.5 * registrationData.price)}`
                            : '-0'}
                      </Td>
                    </Tr>
                    <Tr bg="white">
                      <Td border="1px solid" borderColor={COLORS.maastrichtBlue} color={COLORS.maastrichtBlue}
                          bg="white">Less: 20% Mid-Career Enhanced Subsidy</Td>
                      <Td textAlign="center" border="1px solid" borderColor={COLORS.maastrichtBlue}
                          color={COLORS.maastrichtBlue} bg="white">
                        {(registrationData.nationality === "Singapore Citizen") &&
                        registrationData.birth_age >= 40
                            ? `-${(0.2 * registrationData.price)}`
                            : '-0'}
                      </Td>
                    </Tr>
                    <Tr bg="white">
                      <Td border="1px solid" borderColor={COLORS.maastrichtBlue} color={COLORS.maastrichtBlue}
                          bg="white"><strong>Nett Course Fees:</strong></Td>
                      <Td textAlign="center" border="1px solid" borderColor={COLORS.maastrichtBlue}
                          color={COLORS.maastrichtBlue} bg="white">
                        <strong>
                          {(1 * registrationData.price) -
                              (((registrationData.nationality === "Singapore Citizen" ||
                                  registrationData.nationality === "Singapore Permanent Resident" ||
                                  registrationData.nationality === "Long Term Visitor Pass+ (LTVP+"))
                                  ? (0.5 * registrationData.price)
                                  : 0) -
                              ((registrationData.nationality === "Singapore Citizen") &&
                              (registrationData.birth_age >= 40)
                                  ? (0.2 * registrationData.price)
                                  : 0)}
                        </strong>
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </Box>
            </motion.div>

            <Divider my={6}/>
            <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.3}}
            >
              <Text fontSize="lg" fontWeight="bold" color={COLORS.maastrichtBlue}>
                3. Attendance
              </Text>
              <VStack align="start" spacing={3} mt={4}>
                <Text color={COLORS.maastrichtBlue}>
                  Trainees attending funded courses (SSG and IBF) must take their attendance digitally via the Singpass
                  App for both physical classroom and synchronous e-learning courses.
                </Text>
                <Text color={COLORS.maastrichtBlue}>
                  If a trainee does not meet the minimum attendance requirement (if any), trainees will not be eligible
                  to meet assessment eligibility. Otherwise, trainees are advised to request a rescheduling of training
                  and/or assessment sessions.
                </Text>
              </VStack>
            </motion.div>

            <Divider my={6}/>
            <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.3}}
            >
              <Text fontSize="lg" fontWeight="bold" color={COLORS.maastrichtBlue}>
                4. Refund, Withdrawal and Rescheduling Policy
              </Text>
              <VStack align="start" spacing={3} mt={4}>
                <Text color={COLORS.maastrichtBlue}>
                  Sozcode Pte Ltd strives to offer a fair and transparent refund policy. Please review the options below
                  carefully:
                </Text>
                <Text color={COLORS.maastrichtBlue}>
                  If a trainee does not meet the minimum attendance requirement (if any), trainees will not be eligible
                  to meet assessment eligibility. Otherwise, trainees are advised to request a rescheduling of training
                  and/or assessment sessions.
                </Text>
                <br/>
                <Box
                    overflowX="auto"
                    maxWidth="100%"
                    margin="auto"
                    px={4}
                >
                  <Table
                      variant="simple"
                      size="md"
                      width="100%"
                      whiteSpace="normal"
                      minWidth="800px"
                      height="auto"
                      bg="white"
                      color={COLORS.maastrichtBlue}
                  >
                    <Thead>
                      <Tr bg={COLORS.isabelline}>
                        <Th
                            fontWeight="bold"
                            textAlign="center"
                            color={COLORS.maastrichtBlue}
                            px={4}
                            py={4}
                            width="25%"
                            bg={COLORS.isabelline}
                        >
                          Request Type
                        </Th>
                        <Th
                            fontWeight="bold"
                            textAlign="center"
                            color={COLORS.maastrichtBlue}
                            px={4}
                            py={4}
                            width="35%"
                            bg={COLORS.isabelline}
                        >
                          Timeframe
                        </Th>
                        <Th
                            fontWeight="bold"
                            textAlign="center"
                            color={COLORS.maastrichtBlue}
                            px={4}
                            py={4}
                            width="40%"
                            bg={COLORS.isabelline}
                        >
                          Options for Student
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody bg="white">
                      <Tr bg="white">
                        <Td
                            rowSpan={3}
                            color={COLORS.maastrichtBlue}
                            px={4}
                            py={4}
                            verticalAlign="top"
                            bg="white"
                        >
                          Request for refund/withdrawal
                        </Td>
                        <Td
                            color={COLORS.maastrichtBlue}
                            px={4}
                            py={4}
                            bg="white"
                        >
                          Before course confirmation email is sent
                        </Td>
                        <Td
                            color={COLORS.maastrichtBlue}
                            px={4}
                            py={4}
                            bg="white"
                        >
                          Cancel with no cancellation charges
                        </Td>
                      </Tr>
                      <Tr bg="white">
                        <Td
                            color={COLORS.maastrichtBlue}
                            px={4}
                            py={4}
                            bg="white"
                        >
                          At least 10 business days before the course start date
                        </Td>
                        <Td
                            color={COLORS.maastrichtBlue}
                            px={4}
                            py={4}
                            bg="white"
                        >
                          Cancel with 50% course fees still payable
                        </Td>
                      </Tr>
                      <Tr bg="white">
                        <Td
                            color={COLORS.maastrichtBlue}
                            px={4}
                            py={4}
                            bg="white"
                        >
                          Less than 10 business days before the course start date
                        </Td>
                        <Td
                            color={COLORS.maastrichtBlue}
                            px={4}
                            py={4}
                            bg="white"
                        >
                          Cancel with 100% of the fees payable
                        </Td>
                      </Tr>
                      <Tr bg="white">
                        <Td rowSpan={2} color={COLORS.maastrichtBlue} px={4} py={4} bg="white">
                          Request for rescheduling
                        </Td>
                        <Td color={COLORS.maastrichtBlue} px={4} py={4} bg="white">
                          Recognised Absences with supporting documents:
                          <UnorderedList>
                            <ListItem>Medical</ListItem>
                            <ListItem>Reservist</ListItem>
                            <ListItem>Court appearance</ListItem>
                            <ListItem>Bereavement of immediate family members</ListItem>
                          </UnorderedList>
                        </Td>
                        <Td color={COLORS.maastrichtBlue} px={4} py={4} bg="white">
                          Re-scheduling for other course runs free-of-charge (subject to availability)
                        </Td>
                      </Tr>
                      <Tr bg="white">
                        <Td color={COLORS.maastrichtBlue} px={4} py={4} bg="white">
                          Rescheduling for reasons not included in Recognised Absences
                        </Td>
                        <Td color={COLORS.maastrichtBlue} px={4} py={4} bg="white">
                          Re-scheduling for other course runs at a one-time cost of $100 (subject to availability)
                        </Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
              <VStack align="start" spacing={3} mt={4}>
                <HStack>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue}>a)</Text>
                  <Text color={COLORS.maastrichtBlue}>Sozcode Pte Ltd reserves the right to change or cancel the course
                    due to unforeseen circumstances. A full refund of any course fees paid will be made to the Student
                    in this circumstance.</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue}>b)</Text>
                  <Text color={COLORS.maastrichtBlue}>A withdrawal request can be made via sending an email to
                    courses@sozcode.com.</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue}>c)</Text>
                  <Text color={COLORS.maastrichtBlue}> Refunds for credit card payments will be credited back to the
                    original payment methods. Refunds for PayNow payment will be credited back through PayNow. All valid
                    refunds will be credited back to the Student in an estimated 20 business days.</Text>
                </HStack>
              </VStack>
            </motion.div>

            <Divider my={6}/>
            <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.3}}
            >
              <Text fontSize="lg" fontWeight="bold" color={COLORS.maastrichtBlue}>
                5. Appeal and Re-assessment Process
              </Text>
              <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
                <Image
                    src="https://cdn.prod.website-files.com/61795563005b7a7fcfbcab48/674e723e6f7ad240fd55a19c_Swim%20lane%20diagram%20(Course%20operations)%20-%20Frame%202.jpg"
                    alt="Appeal and Re-assessment Process Flowchart"
                    maxWidth="100%"
                    objectFit="contain"
                />
              </Box>
              <VStack align="start" spacing={3} mt={4}>
                <HStack>
                  <Text color={COLORS.maastrichtBlue}>A non-refundable Appeal Fee of S$60 will apply to each appeal
                    against the Not-Yet-Competent (NYC)' status awarded. The Academic and Examination Board members'
                    decision will be final.</Text>
                </HStack>
                <HStack>
                  <Text color={COLORS.maastrichtBlue}>The Academic and Examination Board members' decision will be
                    final.</Text>
                </HStack>
              </VStack>
            </motion.div>

            <Divider my={6}/>
            <Text color={COLORS.maastrichtBlue}>The parties hereby acknowledge and agree to the terms stated in this
              contract.</Text>
            <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.3}}
            >
              <VStack align="start" spacing={4} mt={6}>
                <Text fontSize="lg" fontWeight="bold" color={COLORS.maastrichtBlue}>
                  Acknowledgment and Signatures
                </Text>
                <Box>
                  <Text color={COLORS.maastrichtBlue}>SIGNED by Sozcode Pte Ltd.</Text>
                  <Divider my={2}/>
                  <Box display="flex" justifyContent="left" alignItems="left" mt={4}>
                    <Image
                        src="https://cdn.prod.website-files.com/61795563005b7a7fcfbcab48/674e723e8d3c5c06545cfe05_Brandon%20Signature.png"
                        alt="Sozcode Signature"
                        maxWidth="30%"
                        objectFit="contain"
                    />
                  </Box>
                  <Text color={COLORS.maastrichtBlue}>__________________________</Text>
                  <Text color={COLORS.maastrichtBlue}>Authorised Signatory</Text>
                  <Text color={COLORS.maastrichtBlue}>Name: Brandon Ong</Text>
                  <Text color={COLORS.maastrichtBlue}>Date: {new Date().toLocaleDateString('en-GB')}</Text>
                  <Divider my={6}/>
                </Box>
              </VStack>
            </motion.div>

            <Text color={COLORS.maastrichtBlue}>SIGNED by the Student</Text>
            <Divider my={2}/>
            <Box
                width="100%"
                maxWidth="100%"
                overflow="hidden"
            >
              <SignaturePad
                  ref={sigPad}
                  canvasProps={{
                    className: "signatureCanvas",
                    height: 200,
                    width: 240,
                    style: {
                      border: `2px solid ${COLORS.maastrichtBlue}`,
                      borderRadius: 'md',
                      maxWidth: '300px'
                    }
                  }}
                  onEnd={() => {
                    setIsSignatureEmpty(sigPad.current?.isEmpty());
                  }}
              />
              <Flex
                  mt={2}
                  mb={8}
                  gap={2}
                  flexWrap="wrap"
                  maxWidth="500px"
              >
                <HStack>
                  <Button
                      flex="1"
                      minWidth="150px"
                      size="sm"
                      variant="outline"
                      onClick={clearSignature}
                      borderColor={COLORS.maastrichtBlue}
                      _hover={{borderColor: COLORS.mandarin}}
                  >
                    Clear Signature
                  </Button>
                </HStack>
                <Box>
                  <Text color={COLORS.maastrichtBlue}>__________________________</Text>
                  <Text color={COLORS.maastrichtBlue}>Name of Student: {registrationData.participant_name}</Text>
                  <Text color={COLORS.maastrichtBlue}>Date: {new Date().toLocaleDateString('en-GB')}</Text>
                </Box>
                <Button
                    flex="1"
                    minWidth="150px"
                    bg={COLORS.mandarin}
                    color="white"
                    size="lg"
                    _hover={{
                      bg: '#e06835',
                    }}
                    _active={{
                      bg: '#d55a29',
                    }}
                    onClick={saveSignatureAndProceed}
                    isLoading={submitting}
                    isDisabled={isSignatureEmpty}
                >
                  Save & Proceed to Checkout
                </Button>
              </Flex>
            </Box>

          </Box>
        </Container>
    );
  }
};
